import React, { useEffect, useMemo, useState } from 'react';
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getToken } from '../authentication/authProvider';

const NEST_API_URL =
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';

type FuneralCoronerRow = {
  id: number;
  funeral_id: number;
  coroner_id: string;
  coroner_name_snapshot: string;
  coroner_address_snapshot: string | null;
  coroner_address_line1: string | null;
  coroner_address_line2: string | null;
  coroner_city: string | null;
  coroner_province: string | null;
  coroner_postal_code: string | null;
};

type CoronerRow = {
  id: string;
  name: string;
  address: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
};

interface OnCallEvent {
  summary: string;
  start: string;
  end: string;
  note: string;
}
interface OnCallResponse {
  fetchedAt: string;
  region: 'toronto-west';
  events: OnCallEvent[];
}

export function SidebarCoronerModal({
  open,
  onClose,
  funeralId,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  existing: FuneralCoronerRow | null;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [create] = useCreate();
  const [update] = useUpdate();

  const [coronerId, setCoronerId] = useState<string | null>(
    existing?.coroner_id ?? null,
  );
  const [saving, setSaving] = useState(false);

  // Calendar date the user wants to inspect. Defaults to today (Toronto).
  const [calendarDate, setCalendarDate] = useState<string>(todayInToronto());

  const [onCall, setOnCall] = useState<OnCallResponse | null>(null);
  const [onCallLoading, setOnCallLoading] = useState(false);
  const [onCallError, setOnCallError] = useState<string | null>(null);

  // Reset selection + date when the modal first opens.
  useEffect(() => {
    if (!open) return;
    setCoronerId(existing?.coroner_id ?? null);
    setCalendarDate(todayInToronto());
  }, [open, existing?.coroner_id]);

  // Fetch the calendar whenever the modal is open and the date changes.
  useEffect(() => {
    if (!open) return;
    setOnCall(null);
    setOnCallError(null);
    setOnCallLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(
          `${NEST_API_URL}coroners/on-call?date=${encodeURIComponent(calendarDate)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as OnCallResponse;
        if (!cancelled) setOnCall(data);
      } catch {
        if (!cancelled) {
          setOnCallError(
            'Calendar unreachable — pick the coroner manually below.',
          );
        }
      } finally {
        if (!cancelled) setOnCallLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, calendarDate]);

  // Search state for the coroner picker. Server-side filter via PostgREST ilike.
  const [search, setSearch] = useState('');
  const filter = useMemo(() => {
    const f: Record<string, unknown> = { active: true };
    if (search.trim()) f['name@ilike'] = `%${search.trim()}%`;
    return f;
  }, [search]);

  const { data: coronerOptions = [], isLoading: optionsLoading } =
    useGetList<CoronerRow>('coroners', {
      filter,
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'name', order: 'ASC' },
    });

  // The selected coroner — needed to snapshot name + address on save. Pulled
  // from the loaded options; falls back to existing snapshot if not in the
  // current page (e.g. an inactive previously-assigned coroner).
  const chosenCoroner: CoronerRow | null = useMemo(() => {
    if (!coronerId) return null;
    const match = coronerOptions.find((c) => c.id === coronerId);
    if (match) return match;
    if (existing && existing.coroner_id === coronerId) {
      return {
        id: existing.coroner_id,
        name: existing.coroner_name_snapshot,
        address: existing.coroner_address_snapshot,
        address_line1: existing.coroner_address_line1,
        address_line2: existing.coroner_address_line2,
        city: existing.coroner_city,
        province: existing.coroner_province,
        postal_code: existing.coroner_postal_code,
      };
    }
    return null;
  }, [coronerId, coronerOptions, existing]);

  const handleSave = async () => {
    if (!coronerId) {
      notify('Pick a coroner first', { type: 'warning' });
      return;
    }
    if (!chosenCoroner) {
      notify('Coroner record is still loading…', { type: 'info' });
      return;
    }
    setSaving(true);
    const payload = {
      funeral_id: funeralId,
      coroner_id: chosenCoroner.id,
      coroner_name_snapshot: chosenCoroner.name,
      coroner_address_snapshot: chosenCoroner.address ?? null,
      coroner_address_line1: chosenCoroner.address_line1 ?? null,
      coroner_address_line2: chosenCoroner.address_line2 ?? null,
      coroner_city: chosenCoroner.city ?? null,
      coroner_province: chosenCoroner.province ?? null,
      coroner_postal_code: chosenCoroner.postal_code ?? null,
      assigned_at: new Date().toISOString(),
      // Supabase auth ids are UUIDs but the column is INTEGER (matches the
      // existing aftercare/review_requests pattern). Coerce to int or null —
      // the human-readable name + email is the actual audit signal.
      assigned_by_user_id: numericId((identity as any)?.id),
      assigned_by_user_name: (identity as any)?.fullName ?? (identity as any)?.email ?? null,
    };
    try {
      if (existing) {
        await update(
          'funeral_coroners',
          { id: existing.id, data: payload, previousData: existing },
          { mutationMode: 'pessimistic' },
        );
      } else {
        await create(
          'funeral_coroners',
          { data: payload },
          { returnPromise: true },
        );
      }
      notify('Coroner saved', { type: 'success' });
      refresh();
      onClose();
    } catch (err: any) {
      notify(`Error saving coroner: ${err?.message ?? 'unknown'}`, {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Select coroner — Toronto West</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ my: 1 }}>
          <Box>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                From the OCC Toronto West public calendar
              </Typography>
              <TextField
                type="date"
                size="small"
                value={calendarDate}
                onChange={(e) => setCalendarDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 170 }}
              />
            </Stack>
            {onCallLoading && (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {onCallError && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                {onCallError}
              </Typography>
            )}
            {onCall && onCall.events.length === 0 && !onCallLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No coverage entries for {calendarDate}. Pick a coroner manually below.
              </Typography>
            )}
            {onCall && onCall.events
              .filter((ev) => !isAlternativeCoverage(ev.summary))
              .map((ev, i) => (
                <EventCard key={`p${i}`} ev={ev} />
              ))}
            {onCall && onCall.events.some((ev) => isAlternativeCoverage(ev.summary)) && (
              <Accordion
                disableGutters
                square
                sx={{
                  mt: 1,
                  boxShadow: 'none',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Alternative Coverage
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {onCall.events
                    .filter((ev) => isAlternativeCoverage(ev.summary))
                    .map((ev, i) => (
                      <EventCard key={`a${i}`} ev={ev} />
                    ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          <Autocomplete<CoronerRow, false, false, false>
            options={coronerOptions}
            value={chosenCoroner}
            onChange={(_, value) => setCoronerId(value ? value.id : null)}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(opt) => opt.name}
            loading={optionsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Coroner"
                placeholder="Search by name…"
                size="small"
              />
            )}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt.id}>
                <Box>
                  <Typography variant="body2">{opt.name}</Typography>
                  {opt.address && (
                    <Typography variant="caption" color="text.secondary">
                      {opt.address}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText={optionsLoading ? 'Loading…' : 'No matches'}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || !coronerId}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function numericId(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isAlternativeCoverage(summary: string): boolean {
  return /^alternative coverage/i.test(summary.trim());
}

function todayInToronto(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
}

function EventCard({ ev }: { ev: OnCallEvent }) {
  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {ev.summary}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {fmtRange(ev.start, ev.end)}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          mt: 0.5,
          fontFamily: 'inherit',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.4,
        }}
      >
        {ev.note}
      </Box>
    </Box>
  );
}

function fmtRange(startISO: string, endISO: string): string {
  const fmt = (d: Date) =>
    d.toLocaleString('en-CA', {
      timeZone: 'America/Toronto',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  return `${fmt(new Date(startISO))} → ${fmt(new Date(endISO))}`;
}

// Hook used by the sidebar Row to fetch the (zero-or-one) existing assignment.
// `isLoading` flips false on either success or error, so callers can render
// the "Select" fallback even when the table hasn't been migrated yet.
export function useFuneralCoroner(funeralId: number | string | undefined) {
  const enabled = !!funeralId;
  const { data, isLoading, error, refetch } = useGetList<FuneralCoronerRow>(
    'funeral_coroners',
    {
      filter: { funeral_id: funeralId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
    },
    { enabled, retry: false },
  );
  return {
    isLoading: isLoading && !error,
    row: (data && data[0]) || null,
    error,
    refetch,
  };
}
